import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  Sparkles, Building2, ShieldCheck, Award, TrendingUp,
  ArrowRight, X, ChevronLeft, ChevronRight, CheckCircle2
} from 'lucide-react';

/**
 * GettingStartedTour - 5-step onboarding modal for newly-registered users.
 *
 * Auto-opens when a user has 0 entities and hasn't completed/dismissed the tour.
 * Tour completion is persisted to localStorage per-user so it only appears once.
 */
const STORAGE_KEY = 'corpcraft_tour_completed_v1';

const STEPS = [
  {
    icon: Sparkles,
    iconColor: 'text-blue-400',
    iconBg: 'bg-blue-500/20',
    title: 'Welcome to CorpCraft',
    body: (
      <>
        <p className="text-slate-300 leading-relaxed">
          You've just joined an AI-powered platform for building, managing, and
          protecting corporate structures — used by family offices, founders,
          and legal teams across the U.S.
        </p>
        <p className="text-slate-400 text-sm mt-4">
          This quick 5-step tour shows you the essentials. Takes about a minute.
        </p>
      </>
    ),
    cta: "Let's go",
  },
  {
    icon: Building2,
    iconColor: 'text-emerald-400',
    iconBg: 'bg-emerald-500/20',
    title: 'Create Your First Entity',
    body: (
      <>
        <p className="text-slate-300 leading-relaxed">
          Entities are the foundation of your corporate structure — LLCs,
          corporations, trusts, and holding companies.
        </p>
        <ul className="mt-4 space-y-2 text-sm text-slate-300">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            AI recommends the best jurisdiction for your industry
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            Track EINs, officers, registered agents automatically
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            Build parent-child hierarchies for holding structures
          </li>
        </ul>
      </>
    ),
    cta: 'Continue',
  },
  {
    icon: ShieldCheck,
    iconColor: 'text-purple-400',
    iconBg: 'bg-purple-500/20',
    title: 'File UCC-1 Financing Statements',
    body: (
      <>
        <p className="text-slate-300 leading-relaxed">
          Protect your assets with automated UCC filings. CorpCraft generates
          fully compliant UCC-1 and UCC-3 forms and submits them to any state.
        </p>
        <p className="text-slate-400 text-sm mt-4">
          Includes continuation, termination, and assignment workflows — plus
          automatic expiration tracking so you never miss a renewal.
        </p>
      </>
    ),
    cta: 'Continue',
  },
  {
    icon: Award,
    iconColor: 'text-amber-400',
    iconBg: 'bg-amber-500/20',
    title: 'Register & File Trademarks',
    body: (
      <>
        <p className="text-slate-300 leading-relaxed">
          Full USPTO TEAS Standard workflow — mark intake, multi-class filings,
          specimen uploads, and attorney-reviewed PDF generation.
        </p>
        <p className="text-slate-400 text-sm mt-4">
          Run a <span className="text-emerald-300 font-medium">free clearance
          search</span> before filing or upgrade to the
          <span className="text-purple-300 font-medium"> $49 Deep-Scan</span>{' '}
          with USPTO TSDR + Madrid Protocol coverage.
        </p>
      </>
    ),
    cta: 'Continue',
  },
  {
    icon: TrendingUp,
    iconColor: 'text-green-400',
    iconBg: 'bg-green-500/20',
    title: 'Your Fundability Score',
    body: (
      <>
        <p className="text-slate-300 leading-relaxed">
          CorpCraft synthesizes D&B Paydex, Nav, SBFE, and your corporate
          structure to produce a single fundability score from 0-100.
        </p>
        <p className="text-slate-400 text-sm mt-4">
          Higher scores unlock better rates from our{' '}
          <span className="text-white font-medium">4+ matched lenders</span> —
          SBA, ABL, LOC, and equipment finance.
        </p>
        <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
          <p className="text-sm text-emerald-200">
            ✨ You're all set. Click "Create Entity" below to start building
            your structure.
          </p>
        </div>
      </>
    ),
    cta: 'Create My First Entity',
  },
];

export default function GettingStartedTour({ userId, onComplete }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;
    const key = `${STORAGE_KEY}_${userId}`;
    const done = localStorage.getItem(key);
    if (!done) {
      // Tiny delay so dashboard renders first
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [userId]);

  const finish = (navigateToCreate = false) => {
    if (userId) {
      localStorage.setItem(`${STORAGE_KEY}_${userId}`, new Date().toISOString());
    }
    setOpen(false);
    onComplete?.();
    if (navigateToCreate) {
      setTimeout(() => navigate('/entities/new'), 200);
    }
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      finish(true);
    }
  };

  const handleBack = () => setStep(Math.max(0, step - 1));

  if (!open) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-6"
      data-testid="getting-started-tour"
      onClick={(e) => e.target === e.currentTarget && finish(false)}
    >
      <div className="bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-700 rounded-2xl max-w-lg w-full shadow-2xl relative">
        {/* Close */}
        <button
          onClick={() => finish(false)}
          data-testid="btn-tour-skip"
          className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800 transition"
          aria-label="Skip tour"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Content */}
        <div className="p-8">
          <div className={`w-14 h-14 rounded-xl ${current.iconBg} flex items-center justify-center mb-5`}>
            <Icon className={`w-7 h-7 ${current.iconColor}`} />
          </div>

          <h2 className="text-2xl font-bold text-white mb-3 font-['Outfit']">
            {current.title}
          </h2>

          <div className="text-base">{current.body}</div>
        </div>

        {/* Progress + nav */}
        <div className="px-8 pb-6 pt-2 border-t border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-1.5" data-testid="tour-progress">
            {STEPS.map((_, idx) => (
              <div
                key={idx}
                className={`h-1.5 rounded-full transition-all ${
                  idx === step
                    ? 'w-6 bg-blue-500'
                    : idx < step
                    ? 'w-1.5 bg-blue-500/60'
                    : 'w-1.5 bg-slate-700'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                data-testid="btn-tour-back"
                className="text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              data-testid="btn-tour-next"
              className={
                isLast
                  ? 'bg-gradient-to-r from-emerald-500 to-blue-500 hover:opacity-90 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }
            >
              {current.cta}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Skip link */}
        {!isLast && (
          <div className="px-8 pb-4 text-center">
            <button
              onClick={() => finish(false)}
              data-testid="btn-tour-skip-link"
              className="text-xs text-slate-500 hover:text-slate-300 transition"
            >
              Skip tour
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
